"use client";

import { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import { toast } from "sonner";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useIdentity } from "@/contexts/IdentityContext";
import { useLanguage } from "@/hooks/useLanguage";
import {
	clearGuestFavoriteTokens,
	dispatchFavoritesUpdated,
	fetchFavoriteTokenIdsForPrincipal,
	readGuestFavoriteTokens,
	type FavoriteTokenSummary,
} from "@/lib/favorites/favorites";
import { createLogAdapter } from "@/lib/logger/console-adapter";
import { OdinFunAuthService } from "@/lib/services/odin-fun-auth";
import { cn } from "@/lib/utils";

const uiLog = createLogAdapter(
	"components/favorites/guest-favorites-merge-manager.tsx",
);

function getPromptStorageKey(principal: string, fingerprint: string) {
	return `guest_favorites_merge_prompt:${principal}:${fingerprint}`;
}

export function GuestFavoritesMergeManager() {
	const { identity, isAuthenticated, principal } = useIdentity();
	const { language } = useLanguage();
	const [open, setOpen] = useState(false);
	const [mergeLoading, setMergeLoading] = useState(false);
	const [guestFavorites, setGuestFavorites] = useState<FavoriteTokenSummary[]>([]);
	const [existingFavoriteIds, setExistingFavoriteIds] = useState<Set<string>>(
		new Set(),
	);
	const [inspectionLoading, setInspectionLoading] = useState(false);
	const [guestFavoritesFingerprint, setGuestFavoritesFingerprint] = useState("");
	const pendingFavorites = guestFavorites.filter(
		(token) => !existingFavoriteIds.has(token.id),
	);
	const skippedFavorites = guestFavorites.filter((token) =>
		existingFavoriteIds.has(token.id),
	);

	const texts = useMemo(
		() => ({
			title: language === "zh" ? "合并游客收藏" : "Merge Guest Favorites",
			description:
				language === "zh"
					? `检测到你有 ${guestFavorites.length} 个游客收藏。可以选择将未存在于账号中的收藏同步到当前账号。`
					: `We found ${guestFavorites.length} guest favorites. You can merge the items that are not already in this account.`,
			pendingCount:
				language === "zh"
					? `将新增 ${pendingFavorites.length} 个收藏`
					: `${pendingFavorites.length} favorites will be added`,
			skippedCount:
				language === "zh"
					? `已有 ${skippedFavorites.length} 个收藏会跳过`
					: `${skippedFavorites.length} favorites already exist and will be skipped`,
			listTitle: language === "zh" ? "待合并收藏" : "Favorites to merge",
			skippedTitle: language === "zh" ? "已存在于账号" : "Already in account",
			emptyPending:
				language === "zh"
					? "当前没有需要新增到账号的收藏。"
					: "There are no new favorites to add.",
			cancel: language === "zh" ? "暂不处理" : "Not now",
			confirm:
				pendingFavorites.length > 0
					? language === "zh"
						? "合并收藏"
						: "Merge favorites"
					: language === "zh"
						? "知道了"
						: "Got it",
			success: language === "zh" ? "游客收藏已合并到账号" : "Guest favorites merged",
			failed: language === "zh" ? "合并收藏失败" : "Failed to merge favorites",
			inspectionFailed:
				language === "zh"
					? "检查账号收藏状态失败，将按新增处理"
					: "Failed to inspect current account favorites; merge will treat all items as new",
		}),
		[guestFavorites, language, pendingFavorites.length, skippedFavorites.length],
	);

	useEffect(() => {
		if (!isAuthenticated || !principal) {
			setOpen(false);
			setGuestFavorites([]);
			setExistingFavoriteIds(new Set());
			return;
		}

		let cancelled = false;

		const inspectFavorites = async () => {
			const nextGuestFavorites = readGuestFavoriteTokens();
			if (nextGuestFavorites.length === 0) {
				if (!cancelled) {
					setOpen(false);
					setGuestFavorites([]);
					setExistingFavoriteIds(new Set());
				}
				return;
			}

			const fingerprint = nextGuestFavorites.map((token) => token.id).join(",");
			const dismissed = window.localStorage.getItem(
				getPromptStorageKey(principal, fingerprint),
			);

			if (!cancelled) {
				setGuestFavorites(nextGuestFavorites);
				setGuestFavoritesFingerprint(fingerprint);
				setInspectionLoading(true);
			}

			try {
				const nextExistingFavoriteIds = await fetchFavoriteTokenIdsForPrincipal(
					principal,
				);
				if (!cancelled) {
					setExistingFavoriteIds(nextExistingFavoriteIds);
				}
			} catch (error) {
				uiLog.error("Failed to inspect account favorites before merge:", error);
				if (!cancelled) {
					setExistingFavoriteIds(new Set());
					toast.error(texts.inspectionFailed);
				}
			} finally {
				if (!cancelled) {
					setInspectionLoading(false);
					setOpen(dismissed !== "dismissed" && dismissed !== "merged");
				}
			}
		};

		void inspectFavorites();
		return () => {
			cancelled = true;
		};
	}, [isAuthenticated, principal, texts.inspectionFailed]);

	const markPromptState = (state: "dismissed" | "merged") => {
		if (!principal || !guestFavoritesFingerprint) return;
		window.localStorage.setItem(
			getPromptStorageKey(principal, guestFavoritesFingerprint),
			state,
		);
	};

	const handleMerge = async () => {
		if (!identity || !principal || mergeLoading) return;
		if (pendingFavorites.length === 0) {
			markPromptState("merged");
			setOpen(false);
			return;
		}

		setMergeLoading(true);

		try {
			let authToken = OdinFunAuthService.getValidToken();
			if (!authToken) {
				authToken = await OdinFunAuthService.getOdinFunAuth(identity);
			}
			if (!authToken) {
				throw new Error("Missing odin.fun auth token");
			}

			for (const token of pendingFavorites) {
				const response = await fetch(
					`https://api.odin.fun/v1/token/${token.id}/favorite`,
					{
						method: "POST",
						headers: {
							Authorization: `Bearer ${authToken}`,
							accept: "application/json",
						},
						body: "",
					},
				);

				if (!response.ok) {
					throw new Error(`Merge favorite failed: ${response.status}`);
				}
			}

			clearGuestFavoriteTokens();
			dispatchFavoritesUpdated({ source: "guest" });
			dispatchFavoritesUpdated({ source: "server", principal });
			markPromptState("merged");
			setOpen(false);
			toast.success(texts.success);
		} catch (error) {
			uiLog.error("Failed to merge guest favorites:", error);
			toast.error(texts.failed);
		} finally {
			setMergeLoading(false);
		}
	};

	const handleOpenChange = (nextOpen: boolean) => {
		setOpen(nextOpen);
		if (!nextOpen) {
			markPromptState("dismissed");
		}
	};

	return (
		<AlertDialog open={open} onOpenChange={handleOpenChange}>
			<AlertDialogContent className="max-w-xl">
				<AlertDialogHeader>
					<AlertDialogTitle>{texts.title}</AlertDialogTitle>
					<AlertDialogDescription>{texts.description}</AlertDialogDescription>
				</AlertDialogHeader>
				<div className="space-y-3">
					<div className="flex flex-wrap gap-2 text-xs">
						<div className="rounded-full bg-emerald-500/10 px-3 py-1 font-medium text-emerald-600 dark:text-emerald-400">
							{texts.pendingCount}
						</div>
						<div className="rounded-full bg-slate-500/10 px-3 py-1 font-medium text-slate-600 dark:text-slate-300">
							{texts.skippedCount}
						</div>
					</div>

					<div className="space-y-2">
						<div className="text-sm font-medium">{texts.listTitle}</div>
						<div className="max-h-56 space-y-2 overflow-y-auto rounded-xl border border-border/60 bg-card/40 p-2">
							{inspectionLoading ? (
								Array.from({ length: 3 }).map((_, index) => (
									<div
										key={index}
										className="flex items-center gap-3 rounded-lg px-2 py-2"
									>
										<div className="h-9 w-9 animate-pulse rounded-full bg-muted" />
										<div className="min-w-0 flex-1">
											<div className="mb-1 h-3 w-20 animate-pulse rounded bg-muted" />
											<div className="h-3 w-28 animate-pulse rounded bg-muted" />
										</div>
									</div>
								))
							) : pendingFavorites.length > 0 ? (
								pendingFavorites.map((token) => (
									<div
										key={token.id}
										className="flex items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-black/[0.03] dark:hover:bg-white/[0.04]"
									>
										<Image
											src={`https://images.odin.fun/token/${token.id}`}
											alt={token.ticker}
											width={36}
											height={36}
											className="rounded-full"
										/>
										<div className="min-w-0 flex-1">
											<div className="truncate text-sm font-medium">
												{token.ticker}
											</div>
											<div className="truncate text-xs text-muted-foreground">
												#{token.id} · {(token.price / 1000).toFixed(3)} Sats
											</div>
										</div>
										<div className="rounded-full bg-emerald-500/10 px-2 py-1 text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
											{language === "zh" ? "将新增" : "Add"}
										</div>
									</div>
								))
							) : (
								<div className="px-2 py-4 text-sm text-muted-foreground">
									{texts.emptyPending}
								</div>
							)}
						</div>
					</div>

					{skippedFavorites.length > 0 ? (
						<div className="space-y-2">
							<div className="text-sm font-medium">{texts.skippedTitle}</div>
							<div className="flex flex-wrap gap-2">
								{skippedFavorites.map((token) => (
									<div
										key={token.id}
										className={cn(
											"rounded-full border border-border/60 px-2.5 py-1 text-xs text-muted-foreground",
										)}
									>
										{token.ticker}
									</div>
								))}
							</div>
						</div>
					) : null}
				</div>
				<AlertDialogFooter>
					<AlertDialogCancel disabled={mergeLoading}>
						{texts.cancel}
					</AlertDialogCancel>
					<AlertDialogAction
						onClick={(event) => {
							event.preventDefault();
							void handleMerge();
						}}
						disabled={mergeLoading}
					>
						{texts.confirm}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}
